import React from 'react';
import { Link } from 'react-router-dom';
import GdprConsentCheckbox from '../components/GdprConsentCheckbox';
import GoogleAddressAutocomplete from '../components/GoogleAddressAutocomplete';
import QuoteRequestSuccess, { QuoteSuccessPrimaryLink } from '../components/QuoteRequestSuccess';
import { useDrivingKmForRoute } from '../hooks/useDrivingKmForRoute';
import { filterHufDigitsInput } from '../utils/hufInput';
import { isValidFlexiblePhone, PHONE_FORMAT_MESSAGE, PHONE_PLACEHOLDER, PHONE_REQUIRED_MESSAGE } from '../utils/phoneValidation';
import QuoteEstimateModal from '../components/QuoteEstimateModal';
import {
  toPositiveNumber,
  volumeM3FromBoxCm,
  computeNetEstimateFromRates,
  formatEstimateFt,
  ESTIMATE_MIN_NET_HUF,
  pricingSnapshot,
} from '../utils/csomagStyleEstimate';
import usePricingSettings from '../hooks/usePricingSettings';
import { shipmentKey, buildShipmentsArray, apiShipmentFromFirst } from '../utils/quoteShipmentForm';
import {
  readFileAsBase64Payload,
  postQuoteInquiry,
  emailWarningsFromInquiryResponse,
} from '../utils/quoteInquiryApi';
import '../App.css';

const MAX_FILE_MB = 10;
/** Legfeljebb ennyi csatolmány sor adható hozzá (az első is opcionális). */
const MAX_ATTACHMENT_SLOTS = 10;
const ACCEPT_EXT = ['.jpg', '.jpeg', '.png', '.pdf'];

const COUNTRIES = [
  'Magyarország',
  'Ausztria',
  'Szlovákia',
  'Románia',
  'Németország',
  'Szlovénia',
  'Horvátország',
  'Egyéb (megjegyzésben)',
];

/** A Google által visszaadott ország is megjelenjen a tiltott selectben. */
function countryOptionsWithSelected(selected) {
  const s = String(selected || '').trim();
  const out = [...COUNTRIES];
  if (s && !out.includes(s)) out.unshift(s);
  return out;
}

const PAYMENT_OPTIONS = [
  { value: '', label: '— Kérjük, válasszon egy opciót —' },
  { value: 'elore', label: 'Fuvar előtt (előre utalás)' },
  { value: 'utan_keszpenz', label: 'Fuvar után készpénzben' },
  { value: 'szamla', label: 'Banki átutalás / számla alapján' },
  { value: 'egyeztetes', label: 'Egyeztetés alapján' },
];

const STEPS = [
  { id: 'route', title: 'Honnan és hová' },
  { id: 'shipment', title: 'A küldemény adatai' },
  { id: 'mode', title: 'Szállítási mód és időpont' },
  { id: 'insurance', title: 'Biztosítás' },
  { id: 'contact', title: 'Ajánlatkérő adatai' },
];

const SHIPMENT_TYPES = [
  { value: 'standard', label: 'standard csomag' },
  { value: 'butor', label: 'bútor' },
  { value: 'kulonleges', label: 'különleges törődést igénylő áruk' },
  { value: 'raklapos', label: 'raklapos áru' },
  { value: 'normal_keretpar', label: 'normál kerékpár' },
  { value: 'ebike', label: 'e-bike' },
  { value: 'motoros', label: 'motorkerékpár' },
  { value: 'egyeb', label: 'egyéb' },
];

const DRAFT_STORAGE_KEY = 'qf:csomagszallitas:draft:v1';

function loadDraft() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Súly/méretek: csak számjegyek; üres megmarad (törölhetsz mindent, majd új számot írsz). */
function filterMeasureDigits(raw) {
  return String(raw ?? '').replace(/\D/g, '');
}

function validateFile(file) {
  if (!file) return null;
  const lower = file.name.toLowerCase();
  const ok = ACCEPT_EXT.some((ext) => lower.endsWith(ext.slice(1)));
  if (!ok) return 'Csak jpg, png vagy pdf tölthető fel.';
  if (file.size > MAX_FILE_MB * 1024 * 1024) return `Max. ${MAX_FILE_MB} MB.`;
  return null;
}

const initialForm = {
  fromAddressSearch: '',
  fromCity: '',
  fromZip: '',
  fromCountry: 'Magyarország',
  fromPlaceId: '',
  toAddressSearch: '',
  toCity: '',
  toZip: '',
  toCountry: 'Magyarország',
  toPlaceId: '',
  distanceKm: '',
  shipmentType: 'standard',
  packagingDesc: '',
  contentDesc: '',
  weightKg: '',
  lengthCm: '',
  widthCm: '',
  heightCm: '',
  shipment2Type: '',
  packaging2Desc: '',
  content2Desc: '',
  weight2Kg: '',
  length2Cm: '',
  width2Cm: '',
  height2Cm: '',
  note2: '',
  note: '',
  deliveryMode: 'normal',
  scheduleType: 'flexible',
  flexFrom: '',
  flexTo: '',
  fixDate: '',
  paymentWhen: '',
  currency: 'HUF',
  insurance: '',
  shipmentValue: '',
  name: '',
  phone: '',
  email: '',
  emailConfirm: '',
  privacyAccepted: false,
};

export default function CsomagszallitasQuotePage() {
  const MAX_SHIPMENTS = 5;
  const { pricingSettings } = usePricingSettings();
  const draft = React.useMemo(() => loadDraft(), []);
  const [step, setStep] = React.useState(() => {
    const savedStep = Number(draft?.step);
    if (!Number.isFinite(savedStep)) return 0;
    return Math.min(Math.max(Math.trunc(savedStep), 0), STEPS.length - 1);
  });
  const [form, setForm] = React.useState(() => ({ ...initialForm, ...(draft?.form || {}) }));
  const [attachmentSlotIds, setAttachmentSlotIds] = React.useState(() => {
    const saved = Array.isArray(draft?.attachmentSlotIds) ? draft.attachmentSlotIds.filter(Boolean) : [];
    return saved.length > 0 ? saved : ['att-0'];
  });
  const [filesBySlot, setFilesBySlot] = React.useState({});
  const [fileErrorsBySlot, setFileErrorsBySlot] = React.useState({});
  const [stepError, setStepError] = React.useState('');
  const [invalidFieldId, setInvalidFieldId] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [submitEmailWarning, setSubmitEmailWarning] = React.useState('');
  const [submitPending, setSubmitPending] = React.useState(false);
  const [estimateOpen, setEstimateOpen] = React.useState(false);
  const [shipmentCount, setShipmentCount] = React.useState(() => {
    const savedCount = Number(draft?.shipmentCount);
    if (!Number.isFinite(savedCount)) return 1;
    return Math.min(Math.max(Math.trunc(savedCount), 1), MAX_SHIPMENTS);
  });

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({
          step,
          form,
          shipmentCount,
          attachmentSlotIds,
        }),
      );
    } catch {
      // Ignore storage failures (private mode / quota), form still works without persistence.
    }
  }, [step, form, shipmentCount, attachmentSlotIds]);

  React.useEffect(() => {
    if (!submitted) return;
    try {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch {
      // no-op
    }
  }, [submitted]);

  const formRef = React.useRef(form);
  formRef.current = form;
  const { distanceLoading, distanceError, calculateDistanceIfReady } = useDrivingKmForRoute(formRef, setForm);

  /** Az utolsó lépésen a távolság gyakran üres marad; megnyitáskor újraszámoljuk a honnan/hová alapján. */
  React.useEffect(() => {
    if (!estimateOpen) return;
    calculateDistanceIfReady(
      form.fromPlaceId,
      form.toPlaceId,
      String(form.fromAddressSearch || '').trim(),
      String(form.toAddressSearch || '').trim(),
    );
  }, [
    estimateOpen,
    form.fromPlaceId,
    form.toPlaceId,
    form.fromAddressSearch,
    form.toAddressSearch,
    calculateDistanceIfReady,
  ]);

  const firstWeight = toPositiveNumber(form.weightKg);
  const firstLength = toPositiveNumber(form.lengthCm);
  const firstWidth = toPositiveNumber(form.widthCm);
  const firstHeight = toPositiveNumber(form.heightCm);
  const distanceKm = Math.max(0, Math.round(toPositiveNumber(form.distanceKm)));
  const volumeM3 = volumeM3FromBoxCm(firstLength, firstWidth, firstHeight);
  const { estimateNet } = computeNetEstimateFromRates(
    firstWeight,
    volumeM3,
    distanceKm,
    pricingSettings.shared,
  );
  const estimatedNetForPayload =
    estimateNet > 0
      ? estimateNet
      : Number(pricingSettings.shared?.missingDistanceFallbackNetHuf || pricingSettings.shared?.minNetHuf || ESTIMATE_MIN_NET_HUF);
  const formatFt = formatEstimateFt;

  const removeShipmentAt = (idxToRemove) => {
    // idxToRemove: 1..shipmentCount-1 (azaz a 2. küldeménytől kezdődően)
    setForm((prev) => {
      const next = { ...prev };
      const baseKeysToShift = ['shipmentType', 'packagingDesc', 'contentDesc', 'weightKg', 'lengthCm', 'widthCm', 'heightCm', 'note'];

      // Shift: idxToRemove..(shipmentCount-2) értékeit felülírjuk a következő mezőkkel
      // (pl. 3. küldemény törlésekor a 4. mezői átjönnek a 3. helyére).
      for (let m = idxToRemove; m < shipmentCount - 1; m += 1) {
        for (const baseKey of baseKeysToShift) {
          next[shipmentKey(baseKey, m)] = prev[shipmentKey(baseKey, m + 1)];
        }
      }

      // Clear the last slot (now that shipmentCount will be decremented).
      const lastIdx = shipmentCount - 1;
      for (const baseKey of baseKeysToShift) {
        next[shipmentKey(baseKey, lastIdx)] = '';
      }
      return next;
    });

    setShipmentCount((c) => Math.max(1, c - 1));
    setStepError('');
  };

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setStepError('');
  };

  const updateMeasure = (key, raw) => {
    update(key, filterMeasureDigits(raw));
  };

  const addAttachmentSlot = () => {
    setAttachmentSlotIds((ids) => {
      if (ids.length >= MAX_ATTACHMENT_SLOTS) return ids;
      return [...ids, `att-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`];
    });
    setStepError('');
  };

  const removeAttachmentSlot = (slotId) => {
    setAttachmentSlotIds((ids) => {
      if (ids.length <= 1) return ids;
      return ids.filter((id) => id !== slotId);
    });
    setFilesBySlot((f) => {
      const next = { ...f };
      delete next[slotId];
      return next;
    });
    setFileErrorsBySlot((e) => {
      const next = { ...e };
      delete next[slotId];
      return next;
    });
    setStepError('');
  };

  const setAttachmentFile = (slotId, file) => {
    if (!file) {
      setFilesBySlot((f) => {
        const next = { ...f };
        delete next[slotId];
        return next;
      });
      setFileErrorsBySlot((e) => {
        const next = { ...e };
        delete next[slotId];
        return next;
      });
      setStepError('');
      return;
    }
    const errMsg = validateFile(file);
    if (errMsg) {
      setFileErrorsBySlot((e) => ({ ...e, [slotId]: errMsg }));
      setStepError('');
      return;
    }
    setFileErrorsBySlot((e) => {
      const next = { ...e };
      delete next[slotId];
      return next;
    });
    setFilesBySlot((f) => ({ ...f, [slotId]: file }));
    setStepError('');
  };

  const firstMissingRouteField = () => {
    if (!String(form.fromAddressSearch || '').trim()) return 'fromAddressSearch';
    if (!form.fromCity.trim()) return 'fromCity';
    if (!form.fromZip.trim()) return 'fromZip';
    if (!form.fromCountry) return 'fromCountry';
    if (!String(form.toAddressSearch || '').trim()) return 'toAddressSearch';
    if (!form.toCity.trim()) return 'toCity';
    if (!form.toZip.trim()) return 'toZip';
    if (!form.toCountry) return 'toCountry';
    return '';
  };

  const scrollToInvalidField = (fieldId) => {
    if (!fieldId) return;
    const el = document.getElementById(fieldId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.focus?.();
  };

  const validateStep = (s) => {
    switch (s) {
      case 0: {
        const missing = firstMissingRouteField();
        if (missing) {
          const routeMessageByField = {
            fromAddressSearch: 'Töltsd ki a felvételi címkereső mezőt.',
            toAddressSearch: 'Töltsd ki a lerakási címkereső mezőt.',
          };
          return {
            message: routeMessageByField[missing] || (missing.startsWith('from') ? 'Töltsd ki a felvételi címet.' : 'Töltsd ki a lerakási címet.'),
            fieldId: missing,
          };
        }
        return { message: '', fieldId: '' };
      }
      case 1:
        for (let idx = 0; idx < shipmentCount; idx += 1) {
          const n = idx + 1;
          const shipmentTypeKey = shipmentKey('shipmentType', idx);
          const packagingKey = shipmentKey('packagingDesc', idx);
          const contentKey = shipmentKey('contentDesc', idx);
          const weightKey = shipmentKey('weightKg', idx);
          const lengthKey = shipmentKey('lengthCm', idx);
          const widthKey = shipmentKey('widthCm', idx);
          const heightKey = shipmentKey('heightCm', idx);

          const shipmentTypeVal = form[shipmentTypeKey] ?? '';
          const packagingVal = form[packagingKey] ?? '';
          const contentVal = form[contentKey] ?? '';
          const weightVal = form[weightKey] ?? '';
          const lengthVal = form[lengthKey] ?? '';
          const widthVal = form[widthKey] ?? '';
          const heightVal = form[heightKey] ?? '';

          if (!shipmentTypeVal) return { message: `Válassz küldemény típust a ${n}. küldeményhez.`, fieldId: '' };
          if (!String(packagingVal).trim()) return { message: `Add meg a csomagolást a ${n}. küldeményhez.`, fieldId: '' };
          if (!String(contentVal).trim()) return { message: `Add meg a tartalmat a ${n}. küldeményhez.`, fieldId: '' };
          if (!String(weightVal).trim()) return { message: `Add meg a súlyt (kg) a ${n}. küldeményhez.`, fieldId: '' };
          if (!String(lengthVal).trim()) return { message: `Add meg a hosszúságot (cm) a ${n}. küldeményhez.`, fieldId: '' };
          if (!String(widthVal).trim()) return { message: `Add meg a szélességet (cm) a ${n}. küldeményhez.`, fieldId: '' };
          if (!String(heightVal).trim()) return { message: `Add meg a magasságot (cm) a ${n}. küldeményhez.`, fieldId: '' };
        }
        for (const id of attachmentSlotIds) {
          if (fileErrorsBySlot[id]) {
            return { message: 'Egy csatolmány nem felel meg a követelményeknek (max. 10 MB, jpg/png/pdf).', fieldId: '' };
          }
        }
        return { message: '', fieldId: '' };
      case 2:
        if (!form.deliveryMode) return { message: 'Válassz szállítási módot.', fieldId: '' };
        if (form.scheduleType === 'flexible') {
          if (!form.flexFrom || !form.flexTo) return { message: 'Add meg a rugalmas felvételi idősáv kezdetét és végét.', fieldId: '' };
        } else if (!form.fixDate) return { message: 'Add meg a fix felvételi napot.', fieldId: '' };
        if (!form.paymentWhen) return { message: 'Válaszd ki, mikor szeretnél fizetni.', fieldId: '' };
        if (!form.currency) return { message: 'Válassz pénznemet.', fieldId: '' };
        return { message: '', fieldId: '' };
      case 3:
        if (form.insurance !== 'igen' && form.insurance !== 'nem') return { message: 'Jelezd, hogy igényelsz-e biztosítást.', fieldId: '' };
        if (form.insurance === 'igen' && !form.shipmentValue.trim()) return { message: 'Add meg a küldemény értékét.', fieldId: '' };
        return { message: '', fieldId: '' };
      case 4:
        if (!form.name.trim()) return { message: 'Add meg a neved.', fieldId: '' };
        if (!form.phone.trim()) return { message: PHONE_REQUIRED_MESSAGE, fieldId: '' };
        if (!isValidFlexiblePhone(form.phone)) {
          return { message: PHONE_FORMAT_MESSAGE, fieldId: '' };
        }
        if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return { message: 'Érvényes e-mail címet adj meg.', fieldId: '' };
        if (form.email !== form.emailConfirm) return { message: 'Az e-mail címek nem egyeznek.', fieldId: '' };
        if (!form.privacyAccepted) return { message: 'Az adatkezelési tájékoztató elfogadása kötelező.', fieldId: '' };
        return { message: '', fieldId: '' };
      default:
        return { message: '', fieldId: '' };
    }
  };

  const next = () => {
    const result = validateStep(step);
    if (result.message) {
      setStepError(result.message);
      setInvalidFieldId(result.fieldId || '');
      setTimeout(() => scrollToInvalidField(result.fieldId), 40);
      return;
    }
    setInvalidFieldId('');
    setStep((x) => Math.min(x + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setStepError('');
    setInvalidFieldId('');
    setStep((x) => Math.max(x - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = validateStep(4);
    if (result.message) {
      setStepError(result.message);
      return;
    }
    setInvalidFieldId('');
    const filesInOrder = attachmentSlotIds.map((id) => filesBySlot?.[id]).filter(Boolean);
    let attachmentUploads = [];
    try {
      attachmentUploads = await Promise.all(filesInOrder.map((f) => readFileAsBase64Payload(f)));
    } catch (readErr) {
      setStepError(readErr?.message || 'Nem sikerült beolvasni a csatolmányokat.');
      return;
    }
    const attachmentNames = filesInOrder.map((f) => f?.name).filter(Boolean);

    const shipments = buildShipmentsArray(form, shipmentCount);
    const first = shipments[0] || {};

    const payload = {
      inquiryType: 'csomagszallitas',
      submittedAt: new Date().toISOString(),
      route: {
        fromAddress: form.fromAddressSearch,
        toAddress: form.toAddressSearch,
        fromCity: form.fromCity,
        fromZip: form.fromZip,
        fromCountry: form.fromCountry,
        toCity: form.toCity,
        toZip: form.toZip,
        toCountry: form.toCountry,
        distanceKm: form.distanceKm || '',
      },
      shipment: apiShipmentFromFirst(first),
      shipments,
      schedule: {
        deliveryMode: form.deliveryMode,
        schedule: form.scheduleType === 'flexible' ? `${form.flexFrom || ''} - ${form.flexTo || ''}` : form.fixDate,
        paymentWhen: form.paymentWhen,
        currency: form.currency,
      },
      insurance: {
        enabled: form.insurance === 'igen',
        shipmentValue: form.shipmentValue || '',
      },
      contact: {
        name: form.name,
        phone: form.phone,
        email: form.email,
      },
      estimate: {
        estimatedTotal: formatFt(estimatedNetForPayload),
        estimatedTotalHuf: Math.round(estimatedNetForPayload),
      },
      pricingSnapshot: pricingSnapshot(pricingSettings),
      attachmentNames,
      attachmentUploads,
    };

    setSubmitPending(true);
    try {
      const data = await postQuoteInquiry({ payload });
      setSubmitEmailWarning(emailWarningsFromInquiryResponse(data));
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error?.name === 'AbortError') {
        setStepError('A küldés túl sokáig tartott. Kérjük, próbáld újra.');
      } else {
        setStepError(error.message || 'Nem sikerült elküldeni az árajánlatkérést.');
      }
    } finally {
      setSubmitPending(false);
    }
  };

  if (submitted) {
    return (
      <main className="qf-page">
        <div className="qf-inner">
          <QuoteRequestSuccess
            title="Köszönjük az ajánlatkérést!"
            subtitle="Az adataidat sikeresen rögzítettük. Munkatársunk hamarosan felveszi veled a kapcsolatot a megadott elérhetőségeken."
            banner={
              submitEmailWarning ? (
                <div className="qf-banner qf-banner--warn" role="status">
                  {submitEmailWarning}
                </div>
              ) : null
            }
            primary={
              <QuoteSuccessPrimaryLink to="/szolgaltatas/csomagszallitas">
                Vissza a csomagszállításhoz
              </QuoteSuccessPrimaryLink>
            }
          />
        </div>
      </main>
    );
  }

  return (
    <main className="qf-page">
      <div className="qf-inner">
        <div className="qf-head">
          <nav className="svc-breadcrumb-trail qf-breadcrumb-trail" aria-label="Útvonal">
            <Link className="svc-breadcrumb-seg" to="/" state={{ scrollTo: 'szolgaltatas' }}>
              Szolgáltatások
            </Link>
            <span className="svc-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <Link className="svc-breadcrumb-seg" to="/szolgaltatas/csomagszallitas">
              Csomagszállítás
            </Link>
            <span className="svc-breadcrumb-sep" aria-hidden="true">
              /
            </span>
            <span className="svc-breadcrumb-current" aria-current="page">
              Árajánlatkérés
            </span>
          </nav>
          <p className="qf-kicker">Ajánlatkérés csomagszállítás esetén</p>
          <h1 className="qf-title">Árajánlatkérés</h1>
          <p className="qf-sub">
            {step + 1}. lépés / {STEPS.length} — {STEPS[step].title}
          </p>
        </div>

        <div className="qf-progress" aria-hidden="true">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`qf-progress-seg${i <= step ? ' qf-progress-seg--active' : ''}`}
            />
          ))}
        </div>

        {stepError && (
          <div className="qf-banner qf-banner--error" role="alert">
            {stepError}
          </div>
        )}

        <form className="qf-form" onSubmit={step === STEPS.length - 1 ? handleSubmit : (e) => e.preventDefault()}>
          {step === 0 && (
            <div className="qf-step">
              <fieldset className="qf-fieldset">
                <legend>HONNAN</legend>
                <div className="qf-grid">
                  <GoogleAddressAutocomplete
                    id="fromAddressSearch"
                    value={form.fromAddressSearch}
                    onResolved={(addr) => {
                      const nextFromPlaceId = addr.placeId || '';
                      const snap = formRef.current;
                      setForm((prev) => ({
                        ...prev,
                        fromAddressSearch: addr.formatted !== undefined ? addr.formatted : prev.fromAddressSearch,
                        fromCity: addr.city || '',
                        fromZip: addr.zip || '',
                        fromCountry: addr.country || '',
                        fromPlaceId: nextFromPlaceId,
                        distanceKm: '',
                      }));
                      calculateDistanceIfReady(
                        nextFromPlaceId,
                        snap.toPlaceId,
                        addr.formatted || '',
                        snap.toAddressSearch || '',
                      );
                    }}
                  />
                  <div className="field qf-field">
                    <label htmlFor="fromCity">Város *</label>
                    <input
                      id="fromCity"
                      className={invalidFieldId === 'fromCity' ? 'qf-invalid' : ''}
                      value={form.fromCity}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field">
                    <label htmlFor="fromZip">Irányítószám *</label>
                    <input
                      id="fromZip"
                      className={invalidFieldId === 'fromZip' ? 'qf-invalid' : ''}
                      value={form.fromZip}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field qf-field--full">
                    <label htmlFor="fromCountry">Ország *</label>
                    <select
                      id="fromCountry"
                      className={invalidFieldId === 'fromCountry' ? 'qf-invalid' : ''}
                      value={form.fromCountry}
                      disabled
                    >
                      {countryOptionsWithSelected(form.fromCountry).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>

              <fieldset className="qf-fieldset">
                <legend>HOVÁ</legend>
                <div className="qf-grid">
                  <GoogleAddressAutocomplete
                    id="toAddressSearch"
                    value={form.toAddressSearch}
                    onResolved={(addr) => {
                      const nextToPlaceId = addr.placeId || '';
                      const snap = formRef.current;
                      setForm((prev) => ({
                        ...prev,
                        toAddressSearch: addr.formatted !== undefined ? addr.formatted : prev.toAddressSearch,
                        toCity: addr.city || '',
                        toZip: addr.zip || '',
                        toCountry: addr.country || '',
                        toPlaceId: nextToPlaceId,
                        distanceKm: '',
                      }));
                      calculateDistanceIfReady(
                        snap.fromPlaceId,
                        nextToPlaceId,
                        snap.fromAddressSearch || '',
                        addr.formatted || '',
                      );
                    }}
                  />
                  {distanceLoading && (
                    <small className="qf-help qf-field--full">Távolság számítása folyamatban…</small>
                  )}
                  {!distanceLoading && !distanceError && distanceKm > 0 && (
                    <small className="qf-help qf-field--full">Számított vezetési távolság: {distanceKm} km</small>
                  )}
                  {!!distanceError && <small className="qf-help qf-help--warn qf-field--full">{distanceError}</small>}
                  <div className="field qf-field">
                    <label htmlFor="toCity">Város *</label>
                    <input
                      id="toCity"
                      className={invalidFieldId === 'toCity' ? 'qf-invalid' : ''}
                      value={form.toCity}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field">
                    <label htmlFor="toZip">Irányítószám *</label>
                    <input
                      id="toZip"
                      className={invalidFieldId === 'toZip' ? 'qf-invalid' : ''}
                      value={form.toZip}
                      readOnly
                      required
                    />
                  </div>
                  <div className="field qf-field qf-field--full">
                    <label htmlFor="toCountry">Ország *</label>
                    <select
                      id="toCountry"
                      className={invalidFieldId === 'toCountry' ? 'qf-invalid' : ''}
                      value={form.toCountry}
                      disabled
                    >
                      {countryOptionsWithSelected(form.toCountry).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </fieldset>
            </div>
          )}

          {step === 1 && (
            <div className="qf-step">
              <h2 className="qf-step-title">A KÜLDEMÉNY ADATAI</h2>
              <div className="field qf-field">
                <label htmlFor="shipmentType">Típus *</label>
                <select
                  id="shipmentType"
                  value={form.shipmentType}
                  onChange={(e) => update('shipmentType', e.target.value)}
                >
                  {SHIPMENT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="qf-grid">
                <div className="field qf-field">
                  <label htmlFor="packagingDesc">Csomagolása *</label>
                  <input
                    id="packagingDesc"
                    value={form.packagingDesc}
                    onChange={(e) => update('packagingDesc', e.target.value)}
                  />
                </div>
                <div className="field qf-field">
                  <label htmlFor="contentDesc">Tartalma *</label>
                  <input
                    id="contentDesc"
                    value={form.contentDesc}
                    onChange={(e) => update('contentDesc', e.target.value)}
                  />
                </div>
              </div>

              <div className="qf-grid">
                <div className="field qf-field">
                  <label htmlFor="weightKg">Súly (kg) *</label>
                  <input
                    id="weightKg"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.weightKg}
                    onChange={(e) => updateMeasure('weightKg', e.target.value)}
                  />
                </div>
                <div className="field qf-field">
                  <label htmlFor="lengthCm">Hosszúság (cm) *</label>
                  <input
                    id="lengthCm"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.lengthCm}
                    onChange={(e) => updateMeasure('lengthCm', e.target.value)}
                  />
                </div>
              </div>

              <div className="qf-grid">
                <div className="field qf-field">
                  <label htmlFor="widthCm">Szélesség (cm) *</label>
                  <input
                    id="widthCm"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.widthCm}
                    onChange={(e) => updateMeasure('widthCm', e.target.value)}
                  />
                </div>
                <div className="field qf-field">
                  <label htmlFor="heightCm">Magasság (cm) *</label>
                  <input
                    id="heightCm"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.heightCm}
                    onChange={(e) => updateMeasure('heightCm', e.target.value)}
                  />
                </div>
              </div>

              <div className="field qf-field">
                <label htmlFor="note">Megjegyzés, egyéb fontos információ</label>
                <textarea
                  id="note"
                  rows={3}
                  value={form.note}
                  onChange={(e) => update('note', e.target.value)}
                />
              </div>

              {Array.from({ length: Math.max(0, shipmentCount - 1) }, (_, k) => k + 1).map((idx) => {
                const n = idx + 1;
                const shipmentTypeKey = shipmentKey('shipmentType', idx);
                const packagingKey = shipmentKey('packagingDesc', idx);
                const contentKey = shipmentKey('contentDesc', idx);
                const weightKey = shipmentKey('weightKg', idx);
                const lengthKey = shipmentKey('lengthCm', idx);
                const widthKey = shipmentKey('widthCm', idx);
                const heightKey = shipmentKey('heightCm', idx);
                const noteKey = shipmentKey('note', idx);

                return (
                  <div key={idx}>
                    <h2 className="qf-step-title qf-step-title--spaced">{n}. KÜLDEMÉNY</h2>

                    <div className="field qf-field">
                      <label htmlFor={`shipment${n}Type`}>Típus *</label>
                      <select
                        id={`shipment${n}Type`}
                        value={form[shipmentTypeKey] ?? ''}
                        onChange={(e) => update(shipmentTypeKey, e.target.value)}
                      >
                        {SHIPMENT_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="qf-grid">
                      <div className="field qf-field">
                        <label htmlFor={`packaging${n}Desc`}>Csomagolása *</label>
                        <input
                          id={`packaging${n}Desc`}
                          value={form[packagingKey] ?? ''}
                          onChange={(e) => update(packagingKey, e.target.value)}
                        />
                      </div>
                      <div className="field qf-field">
                        <label htmlFor={`content${n}Desc`}>Tartalma *</label>
                        <input
                          id={`content${n}Desc`}
                          value={form[contentKey] ?? ''}
                          onChange={(e) => update(contentKey, e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="qf-grid">
                      <div className="field qf-field">
                        <label htmlFor={`weight${n}Kg`}>Súly (kg) *</label>
                        <input
                          id={`weight${n}Kg`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={form[weightKey] ?? ''}
                          onChange={(e) => updateMeasure(weightKey, e.target.value)}
                        />
                      </div>
                      <div className="field qf-field">
                        <label htmlFor={`length${n}Cm`}>Hosszúság (cm) *</label>
                        <input
                          id={`length${n}Cm`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={form[lengthKey] ?? ''}
                          onChange={(e) => updateMeasure(lengthKey, e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="qf-grid">
                      <div className="field qf-field">
                        <label htmlFor={`width${n}Cm`}>Szélesség (cm) *</label>
                        <input
                          id={`width${n}Cm`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={form[widthKey] ?? ''}
                          onChange={(e) => updateMeasure(widthKey, e.target.value)}
                        />
                      </div>
                      <div className="field qf-field">
                        <label htmlFor={`height${n}Cm`}>Magasság (cm) *</label>
                        <input
                          id={`height${n}Cm`}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={form[heightKey] ?? ''}
                          onChange={(e) => updateMeasure(heightKey, e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="field qf-field">
                      <label htmlFor={`note${n}`}>Megjegyzés a {n}. küldeményhez</label>
                      <textarea
                        id={`note${n}`}
                        rows={3}
                        value={form[noteKey] ?? ''}
                        onChange={(e) => update(noteKey, e.target.value)}
                      />
                    </div>

                    <button
                      type="button"
                      className="secondary-btn qf-shipment-remove-btn"
                      onClick={() => removeShipmentAt(idx)}
                    >
                      Küldemény törlése
                    </button>
                  </div>
                );
              })}

              {shipmentCount < MAX_SHIPMENTS && (
                <button
                  type="button"
                  className="secondary-btn qf-shipment-add-btn"
                  onClick={() => {
                    setShipmentCount((c) => Math.min(c + 1, MAX_SHIPMENTS));
                    setStepError('');
                  }}
                >
                  + Még egy küldemény
                </button>
              )}

              <div className="qf-files">
                <p className="qf-files-hint">
                  Kép vagy dokumentum feltöltése <strong>opcionális</strong>. Formátum: jpg, png, pdf;
                  max. {MAX_FILE_MB} MB / fájl. Legfeljebb {MAX_ATTACHMENT_SLOTS} fájl adható hozzá.
                </p>
                {attachmentSlotIds.map((slotId, index) => (
                  <div className="field qf-field qf-file-row" key={slotId}>
                    <label htmlFor={slotId}>Csatolmány {index + 1} (nem kötelező)</label>
                    <div className="qf-file-row-actions">
                      <input
                        id={slotId}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => setAttachmentFile(slotId, e.target.files?.[0] ?? null)}
                      />
                      {attachmentSlotIds.length > 1 && (
                        <button
                          type="button"
                          className="qf-file-remove"
                          onClick={() => removeAttachmentSlot(slotId)}
                        >
                          Sor törlése
                        </button>
                      )}
                    </div>
                    {filesBySlot[slotId] && (
                      <span className="qf-file-name">{filesBySlot[slotId].name}</span>
                    )}
                    {fileErrorsBySlot[slotId] && (
                      <span className="qf-file-err">{fileErrorsBySlot[slotId]}</span>
                    )}
                  </div>
                ))}
                {attachmentSlotIds.length < MAX_ATTACHMENT_SLOTS && (
                  <button type="button" className="secondary-btn qf-add-file" onClick={addAttachmentSlot}>
                    + Még egy fájl
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="qf-step">
              <div className="field qf-field">
                <label htmlFor="deliveryMode">Szállítási mód *</label>
                <select id="deliveryMode" value={form.deliveryMode} onChange={(e) => update('deliveryMode', e.target.value)}>
                  <option value="normal">
                    Normál szállítás — Szállítás az első zárt ajtóig/kapuig, a sofőrnek segítségre van
                    szüksége a nehéz és terjedelmes tárgyakhoz.
                  </option>
                  <option value="inhome">
                    Rakodás / rakodási segítség — házon belül; a sofőr egyedül jár, a rakodáshoz segítség
                    szükséges
                  </option>
                </select>
              </div>

              <h2 className="qf-step-title qf-step-title--spaced">SZÁLLÍTÁSI IDŐ ÉS FIZETÉS</h2>

              <div className="qf-schedule">
                <div className="field qf-field">
                  <label htmlFor="scheduleType">Felvétel típusa *</label>
                  <select
                    id="scheduleType"
                    value={form.scheduleType}
                    onChange={(e) => update('scheduleType', e.target.value)}
                  >
                    <option value="flexible">Rugalmas (felvétel)</option>
                    <option value="fixed">Fix nap (felvétel)</option>
                  </select>
                </div>
                {form.scheduleType === 'flexible' && (
                  <div className="qf-date-row">
                    <div className="field qf-field">
                      <label htmlFor="flexFrom">-tól</label>
                      <input
                        id="flexFrom"
                        type="date"
                        value={form.flexFrom}
                        onChange={(e) => update('flexFrom', e.target.value)}
                      />
                    </div>
                    <div className="field qf-field">
                      <label htmlFor="flexTo">-ig</label>
                      <input
                        id="flexTo"
                        type="date"
                        value={form.flexTo}
                        onChange={(e) => update('flexTo', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                {form.scheduleType === 'fixed' && (
                  <div className="field qf-field">
                    <label htmlFor="fixDate">Dátum</label>
                    <input
                      id="fixDate"
                      type="date"
                      value={form.fixDate}
                      onChange={(e) => update('fixDate', e.target.value)}
                    />
                  </div>
                )}
              </div>

              <div className="field qf-field">
                <label htmlFor="paymentWhen">Mikor szeretnél fizetni? *</label>
                <select
                  id="paymentWhen"
                  value={form.paymentWhen}
                  onChange={(e) => update('paymentWhen', e.target.value)}
                >
                  {PAYMENT_OPTIONS.map((o) => (
                    <option key={o.value || 'empty'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field qf-field">
                <label htmlFor="currency">Pénznem *</label>
                <select id="currency" value={form.currency} onChange={(e) => update('currency', e.target.value)}>
                  <option value="HUF">HUF</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="qf-step">
              <h2 className="qf-step-title">BIZTOSÍTÁS</h2>
              <fieldset className="qf-fieldset qf-fieldset--plain">
                <legend>Igényel biztosítást *</legend>
                <div className="qf-inline-radios">
                  <label className="qf-radio-inline">
                    <input
                      type="radio"
                      name="insurance"
                      value="igen"
                      checked={form.insurance === 'igen'}
                      onChange={() => update('insurance', 'igen')}
                    />
                    igen
                  </label>
                  <label className="qf-radio-inline">
                    <input
                      type="radio"
                      name="insurance"
                      value="nem"
                      checked={form.insurance === 'nem'}
                      onChange={() => update('insurance', 'nem')}
                    />
                    nem
                  </label>
                </div>
              </fieldset>

              {form.insurance === 'igen' && (
                <div className="field qf-field">
                  <label htmlFor="shipmentValue">Küldemény értéke (igen válasz esetén) *</label>
                  <input
                    id="shipmentValue"
                    inputMode="numeric"
                    autoComplete="off"
                    value={form.shipmentValue}
                    onChange={(e) => update('shipmentValue', filterHufDigitsInput(e.target.value))}
                    placeholder="pl. 500000 (csak számjegyek)"
                  />
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="qf-step">
              <h2 className="qf-step-title">AJÁNLATKÉRŐ ADATAI</h2>
              <div className="field qf-field">
                <label htmlFor="name">Név *</label>
                <input id="name" value={form.name} onChange={(e) => update('name', e.target.value)} />
              </div>
              <div className="field qf-field">
                <label htmlFor="phone">Telefonszám *</label>
                <input
                  id="phone"
                  type="tel"
                  placeholder={PHONE_PLACEHOLDER}
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </div>
              <div className="field qf-field">
                <label htmlFor="email">E-mail cím *</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                />
              </div>
              <div className="field qf-field">
                <label htmlFor="emailConfirm">E-mail cím megerősítése *</label>
                <input
                  id="emailConfirm"
                  type="email"
                  autoComplete="email"
                  value={form.emailConfirm}
                  onChange={(e) => update('emailConfirm', e.target.value)}
                />
              </div>

              <GdprConsentCheckbox
                id="csomag-gdpr"
                checked={form.privacyAccepted}
                onChange={(v) => update('privacyAccepted', v)}
              />
            </div>
          )}

          <div className="qf-actions">
            {step > 0 && (
              <button type="button" className="secondary-btn qf-btn-back" onClick={back}>
                Vissza
              </button>
            )}
            {step < STEPS.length - 1 && (
              <button type="button" className="primary-btn qf-btn-next" onClick={next}>
                Tovább
              </button>
            )}
            {step === STEPS.length - 1 && (
              <>
                <button type="button" className="secondary-btn qf-btn-preview" onClick={() => setEstimateOpen(true)}>
                  Előzetes irányár megtekintése
                </button>
              <button type="submit" className="primary-btn qf-btn-submit" disabled={submitPending}>
                {submitPending ? 'Küldés...' : 'Ajánlatkérés küldése'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
      <QuoteEstimateModal
        open={estimateOpen}
        onClose={() => setEstimateOpen(false)}
        distanceLoading={distanceLoading}
        distanceError={distanceError}
        distanceKm={distanceKm}
        weightKg={firstWeight}
        lengthCm={firstLength}
        widthCm={firstWidth}
        heightCm={firstHeight}
        volumeM3={volumeM3}
        volumeMode="box"
        estimateNet={estimateNet}
      />
    </main>
  );
}
